import { useState } from 'react';
import { RUTAS } from '@/api/rutas';

import { Download, Image as ImageIcon } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

const DIRECCION = RUTAS.catalogo.excel;

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  titulo?: string;
}

type Estado = 'con' | 'sin' | null;

/**
 * Baja el catálogo en Excel, con o sin fotos.
 *
 * Qué columnas trae lo decide el servidor según quién pide: con sesión salen los precios y sin
 * sesión no. Acá no se filtra nada, y es a propósito — un filtro del lado del navegador es una
 * cortina, no una puerta.
 */
export function DescargarExcel({ abierto, onCerrar, titulo = 'Descargar Excel' }: Props) {
  const [generando, setGenerando] = useState<Estado>(null);
  const [error, setError] = useState<string | null>(null);

  async function bajar(conImagenes: boolean) {
    setError(null);
    setGenerando(conImagenes ? 'con' : 'sin');

    try {
      const respuesta = await fetch(conImagenes ? `${DIRECCION}?images=1` : DIRECCION, {
        credentials: 'include',
      });

      if (!respuesta.ok) {
        const cuerpo = await respuesta.json().catch(() => null);
        throw new Error(cuerpo?.error ?? `Error ${respuesta.status}`);
      }

      const nombre =
        /filename="([^"]+)"/.exec(respuesta.headers.get('content-disposition') ?? '')?.[1] ??
        'catalogo.xlsx';

      const url = URL.createObjectURL(await respuesta.blob());
      const enlace = document.createElement('a');

      enlace.href = url;
      enlace.download = nombre;
      document.body.append(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);

      onCerrar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'Error al descargar el archivo');
    } finally {
      setGenerando(null);
    }
  }

  function cerrar() {
    if (generando) return;
    setError(null);
    onCerrar();
  }

  const trabajando = generando !== null;

  return (
    <Dialog open={abierto} onClose={cerrar} aria-labelledby="titulo-excel" maxWidth="xs" fullWidth>
      <DialogTitle id="titulo-excel">{titulo}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          ¿Querés incluir las imágenes de los productos en el archivo?
        </DialogContentText>

        <Alert severity="info" sx={{ mb: 1 }}>
          Con imágenes el archivo es más pesado y la generación puede demorar entre 10 y 30
          segundos.
        </Alert>

        {generando === 'con' && (
          <Alert severity="warning" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
            Generando archivo con imágenes… no cierres esta ventana.
          </Alert>
        )}

        {generando === 'sin' && (
          <Alert severity="warning" icon={<CircularProgress size={18} />} sx={{ mt: 2 }}>
            Generando archivo…
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
          <Button
            onClick={() => void bajar(true)}
            color="primary"
            variant="contained"
            startIcon={
              generando === 'con' ? <CircularProgress size={18} color="inherit" /> : <ImageIcon />
            }
            disabled={trabajando}
            fullWidth
          >
            {generando === 'con' ? 'Generando…' : 'Descargar con imágenes'}
          </Button>

          <Button
            onClick={() => void bajar(false)}
            color="primary"
            variant="outlined"
            startIcon={
              generando === 'sin' ? <CircularProgress size={18} color="inherit" /> : <Download />
            }
            disabled={trabajando}
            fullWidth
          >
            {generando === 'sin' ? 'Generando…' : 'Descargar sin imágenes'}
          </Button>

          <Button onClick={cerrar} color="inherit" size="small" disabled={trabajando}>
            Cancelar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
